import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { Sprite, Texture } from 'pixi.js';
import { symbolsConfig, MAX_POOL_SIZE, initialPoolSize } from '../config/symbolsConfigs';

const spritePool: Record<number, Sprite[]> = {};
const spinePool: Record<number, Spine[]> = {};
const spineInstanceCounts: Record<number, number> = {}; 
const allSpineInstances: Spine[] = []; 
const allSpriteInstances: Sprite[] = []; 
const spriteInstanceCounts: Record<number, number> = {}; 

export function releaseSymbolToPool(obj: Sprite | Spine): void {
    const symbolId = (obj as any).symbolId;

    if (symbolId === undefined) {
        obj.destroy();
        return;
    }

    obj.visible = false;
    obj.removeFromParent();

    if (obj instanceof Sprite) {
        if (!spritePool[symbolId]) spritePool[symbolId] = [];
        if (spritePool[symbolId].length >= MAX_POOL_SIZE) {
            obj.destroy();
        } else {
            spritePool[symbolId].push(obj);
        }
    }
    else if (obj instanceof Spine) {
        if (!spinePool[symbolId]) spinePool[symbolId] = [];

        if (spinePool[symbolId].length >= MAX_POOL_SIZE) {
            obj.destroy();
        } else {
            obj.skeleton.setToSetupPose();
            spinePool[symbolId].push(obj);
        }
    }
}

export function getPooledSymbol(symbolIndex: number, isFullRes?: boolean): Sprite {
    const symbol = symbolsConfig[symbolIndex];
    if (!symbol) return new Sprite(Texture.EMPTY);

    const textureName = isFullRes ? symbol.staticSymName : symbol.blurSymName;
    const texture = Texture.from(textureName || "");

    if (spritePool[symbolIndex] && spritePool[symbolIndex].length > 0) {
        const sprite = spritePool[symbolIndex].pop()!;
        sprite.texture = texture;
        sprite.visible = true;
        return sprite;
    }

    const currentTotal = spriteInstanceCounts[symbolIndex] || 0;
    if (currentTotal >= MAX_POOL_SIZE) {
        console.warn(`Cannot create Sprite for ID ${symbolIndex}: MAX_POOL_SIZE (${MAX_POOL_SIZE}) reached.`);
        return new Sprite(Texture.EMPTY);
    }

    const sprite = new Sprite(texture);
    (sprite as any).symbolId = symbolIndex;
    spriteInstanceCounts[symbolIndex] = currentTotal + 1;
    allSpriteInstances.push(sprite);

    return sprite;
}

export function getPooledSpineSymbol(symbolIndex: number): Spine | null {
    const symbol = symbolsConfig[symbolIndex];
    if (!symbol) return null;

    if (spinePool[symbolIndex]?.length > 0) {
        const spineSymbol = spinePool[symbolIndex].pop()!;
        spineSymbol.visible = true;
        return spineSymbol;
    }

    const currentTotal = spineInstanceCounts[symbolIndex] || 0;
    if (currentTotal >= MAX_POOL_SIZE) {
        console.warn(`Limit reached for ID ${symbolIndex}: ${MAX_POOL_SIZE} instances exist.`);
        return null;
    }

    const spineSymbol = Spine.from({
        skeleton: symbol.skeleton,
        atlas: symbol.atlas,
    });
    
    (spineSymbol as any).symbolId = symbolIndex;
    
    spineInstanceCounts[symbolIndex] = currentTotal + 1;
    allSpineInstances.push(spineSymbol);

    return spineSymbol;
}

export function createPools() {
    Object.keys(symbolsConfig).forEach((key) => {
        const id = parseInt(key);
        const config = symbolsConfig[id];
        for (let i = 0; i < initialPoolSize; i++) {
            const s = new Sprite(Texture.EMPTY);
            (s as any).symbolId = id;
            if (!spritePool[id]) spritePool[id] = [];
            spritePool[id].push(s);

            if (config.skeleton) {
                const sp = Spine.from({ skeleton: config.skeleton, atlas: config.atlas });
                (sp as any).symbolId = id;
                
                if (!spinePool[id]) spinePool[id] = [];
                spinePool[id].push(sp);
                
                allSpineInstances.push(sp);
                spineInstanceCounts[id] = (spineInstanceCounts[id] || 0) + 1;
            }
        }
    });
}

export function clearPools() {
    const clearType = (
        instances: { destroy: (options: any) => void }[],
        pool: Record<number, any[]>,
        counts: Record<number, number>
    ) => {
        instances.forEach(item => item.destroy({ children: true }));
        instances.length = 0;

        Object.keys(pool).forEach(id => {
            const numericId = parseInt(id);
            pool[numericId] = [];
        });

        Object.keys(counts).forEach(id => {
            const numericId = parseInt(id);
            counts[numericId] = 0;
        });
    };

    clearType(allSpineInstances, spinePool, spineInstanceCounts);
    clearType(allSpriteInstances, spritePool, spriteInstanceCounts);
}
